import React, {useEffect, useRef, useState} from 'react';
import {Box, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import {embedDashboard} from '@superset-ui/embedded-sdk';
import type {EmbeddedDashboard} from '../../types/chatTypes';
import {useStores} from '../../../../stores';

type Props = {
    dashboard: EmbeddedDashboard;
    /** Fullscreen = dashboard takes the whole page and the chat is hidden. */
    isFullscreen: boolean;
    onToggleFullscreen: () => void;
};

/**
 * Right-side panel rendering a Superset dashboard produced by the dashgen agent.
 * Mounts an iframe via @superset-ui/embedded-sdk; guest tokens are short-lived
 * and re-fetched by the SDK through ChatApiStore.getSupersetGuestToken.
 */
export const SupersetDashboardPanel: React.FC<Props> = React.memo(({dashboard, isFullscreen, onToggleFullscreen}) => {
    const {chatApiStore} = useStores();
    const mountRef = useRef<HTMLDivElement>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const mountPoint = mountRef.current;
        if (!mountPoint) return;

        let disposed = false;
        let unmount: (() => void) | undefined;

        embedDashboard({
            id: dashboard.uuid,
            supersetDomain: dashboard.superset_domain,
            mountPoint,
            fetchGuestToken: () => chatApiStore.getSupersetGuestToken(dashboard.uuid),
            dashboardUiConfig: {
                hideTitle: true,
                filters: {expanded: false},
            },
        }).then((embedded) => {
            if (disposed) {
                embedded.unmount();
                return;
            }
            unmount = embedded.unmount;
        }).catch((err) => {
            console.error('[SupersetDashboardPanel] Failed to embed dashboard:', err);
            if (!disposed) {
                setError('Could not load the dashboard here. Use the dashboard link in the chat instead.');
            }
        });

        return () => {
            disposed = true;
            try {
                unmount?.();
            } catch {
                /* iframe already removed */
            }
            mountPoint.innerHTML = '';
        };
    }, [dashboard.uuid, dashboard.superset_domain, chatApiStore]);

    return (
        <Stack
            className="chat-dashboard-panel"
            sx={{
                width: isFullscreen ? '100%' : '45%',
                minWidth: isFullscreen ? undefined : 420,
                flexShrink: 0,
                height: '100%',
                borderLeft: isFullscreen ? 0 : 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                sx={{px: 1.5, py: 1, gap: 1, borderBottom: 1, borderColor: 'divider'}}
            >
                <Typography variant="subtitle2" noWrap sx={{flexGrow: 1}}>
                    {dashboard.title || 'Dashboard'}
                </Typography>
                <Tooltip title={isFullscreen ? 'Show chat' : 'Full screen'}>
                    <IconButton size="small" onClick={onToggleFullscreen}>
                        {isFullscreen ? <FullscreenExitIcon fontSize="small"/> : <FullscreenIcon fontSize="small"/>}
                    </IconButton>
                </Tooltip>
            </Stack>
            {error ? (
                <Box sx={{p: 2}}>
                    <Typography variant="body2" color="text.secondary">{error}</Typography>
                </Box>
            ) : (
                <Box
                    ref={mountRef}
                    sx={{
                        flexGrow: 1,
                        minHeight: 0,
                        '& iframe': {width: '100%', height: '100%', border: 0, display: 'block'},
                    }}
                />
            )}
        </Stack>
    );
});
SupersetDashboardPanel.displayName = 'SupersetDashboardPanel';
